import { useEffect, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import * as DocumentApiModels from '@typings/document-api-models';
import * as format from '@format';
import FileType from '@components/Icons/FileType';
import PencilSimple from '@components/Icons/PencilSimple';
import Eye from '@components/Icons/Eye';
import CustomLink from '@components/CustomLink';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import XCircle from '@components/Icons/XCircle';
import Tooltip from '@components/Tooltip';
import { usePrevious } from '@hooks';
import s from './DocumentsListModal.module.scss';

type Props = {
  creatorSlug: string;
  compiledDocument: Pick<ApiModels.UserWorkflowDocument,
    | 'id'
    | 'rank'
    | 'title'
    | 'referenceId'
    | 'referenceType'
  > & {
    document: Pick<DocumentApiModels.Document,
      | 'id'
      | 'mimeType'
      | 'fileName'
      | 'fileSize'
     > | null;
  };
  onChangeTitle?: (documentId: string, title: string) => void;
  hasDivider?: boolean;
  isSaving?: boolean;
};
const DocumentRow: React.FC<Props> = ({ creatorSlug, hasDivider, compiledDocument, onChangeTitle, isSaving }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [originalTitle, setOriginalTitle] = useState<string | null>(null);
  const previousIsSaving = usePrevious(isSaving ?? false);

  useEffect(() => {
    if (!previousIsSaving && isSaving) {
      setIsEditingTitle(false);
    }
  }, [previousIsSaving, isSaving]);

  const handleClickEdit = (): void => {
    setIsEditingTitle(true);
    setOriginalTitle(compiledDocument.title);
  };

  const handleClickCancel = (): void => {
    setIsEditingTitle(false);
    if (onChangeTitle) {
      onChangeTitle(compiledDocument.id, originalTitle || '');
    }
  };

  const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (onChangeTitle) {
      onChangeTitle(compiledDocument.id, e.target.value);
    }
  };

  let detailsLink: string | undefined;

  if (compiledDocument?.referenceType === 'document-request-file' && compiledDocument.referenceId) {
    detailsLink = getUrl(IDENTIFIERS.DOCUMENT_REQUEST_DOCUMENT_PREVIEW, {
      creatorSlug,
      documentRequestUserFileId: compiledDocument.referenceId,
    });
  } else if (compiledDocument && compiledDocument.document) {
    detailsLink = getUrl(IDENTIFIERS.DOCUMENT_BY_ID, { creatorSlug, documentId: compiledDocument.document.id });
  }

  return (
    <>
      {hasDivider && <div className={s['horizontal-divider']}></div>}
      <div className={s['row-container']}>
        <div className={s.icon}>
          <FileType mimeType={compiledDocument.document?.mimeType} size={24} />
        </div>
        <div className={s['row-main-part']}>
          <>
            {isEditingTitle
              ? (
                <div className={s['name-edit-wrapper']}>
                  <button onClick={handleClickCancel}>
                    <XCircle weight="fill" size={16} color="var(--color-error)" />
                  </button>
                  <input type="text" id={`${compiledDocument.id}-title`} onChange={handleChangeTitle} defaultValue={compiledDocument.title || ''} />
                </div>
              )
              : (
                <div className={s['name-view-wrapper']}>
                  {(compiledDocument.title || compiledDocument.document?.fileName || compiledDocument.id).length > 80
                    ? (
                      <Tooltip text={compiledDocument.title || compiledDocument.document?.fileName || compiledDocument.id} className={s.tooltip}>
                        <p className={s.name}>{compiledDocument.title || compiledDocument.document?.fileName}</p>
                      </Tooltip>
                    )
                    : (
                      <p className={s.name}>{compiledDocument.title || compiledDocument.document?.fileName}</p>
                    )}
                  <button onClick={handleClickEdit}>
                    <PencilSimple weight="fill" size={16} color="var(--color-gray-100)" />
                  </button>
                </div>
              )}
            <small className={s['file-size']}>{format.fileSize({ as: 'kb', size: compiledDocument.document?.fileSize ?? 0 })}</small>
          </>
        </div>
        {detailsLink && (
          <CustomLink
            className={s['circle-button']}
            newTab
            to={detailsLink}
          >
            <Eye size={16} weight="fill" />
          </CustomLink>
        )}
      </div>
    </>
  );
};

export default DocumentRow;
